import {
  createAddressTitle,
  getDisplayPrice,
  getLatestPrice,
  uploadFileToSupabase,
  type AssetRelationType,
  type FileType,
  type ParsedAsset,
} from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useMutation } from '@tanstack/react-query'
import { randomUUID } from 'expo-crypto'
import { useRouter } from 'expo-router'
import { useCallback, useEffect, useMemo } from 'react'
import { ActivityIndicator, View } from 'react-native'
import { z } from 'zod/v4'
import LoadingAsset from '~/components/assets/LoadingAsset.tsx'
import PropertyCard from '~/components/assets/PropertyCard.tsx'
import { createFlowPhases, FlowProvider } from '~/components/flows/FlowProvider.tsx'
import {
  DataEntryMethodPhase,
  dataEntryMethodSchema,
} from '~/components/flows/phases/DataEntryMethodPhase.tsx'
import {
  DocumentInputPhase,
  documentInputSchema,
} from '~/components/flows/phases/DocumentInputPhase.tsx'
import { ManualPropertyInputPhase } from '~/components/flows/phases/ManualPropertyInputPhase.tsx'
import { MediaPhase, mediaPhaseSchema } from '~/components/flows/phases/MediaPhase'
import { PhaseWrapper } from '~/components/flows/phases/PhaseWrapper.tsx'
import { PricingPhase, pricingSchema } from '~/components/flows/phases/PricingPhase'
import { UrlInputPhase, urlInputSchema } from '~/components/flows/phases/UrlInputPhase.tsx'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { useAlert } from '~/components/GlobalAlert.tsx'
import { ViewTitle } from '~/components/layout/Sections.tsx'
import { Body } from '~/components/typography/Content.tsx'
import { Text } from '~/components/ui/text'
import { useUpdateAsset } from '~/data/assets/useAssets.ts'
import { usePendingAsset } from '~/data/assets/usePendingAsset.ts'
import { propertyInputFormSchema } from '~/lib/propertyFormSchemas'
import { supabase } from '~/lib/supabase.tsx'
import { trpc } from '~/lib/trpc'

const assetExtractionSchema = z.object({
  assetId: z.string(),
  isExtracted: z.literal(true),
})

const extractionFlowDataSchema = z.object({
  'data-entry-method': dataEntryMethodSchema,
  'url-input': urlInputSchema.optional(),
  'document-input': documentInputSchema.optional(),
  'asset-extraction': assetExtractionSchema,
  pricing: pricingSchema,
  media: mediaPhaseSchema,
})

const manualFlowDataSchema = z.object({
  'data-entry-method': dataEntryMethodSchema,
  'manual-input': propertyInputFormSchema,
})

const flowDataSchema = z.union([extractionFlowDataSchema, manualFlowDataSchema])

type AddPropertyFlowData = Partial<
  z.infer<typeof extractionFlowDataSchema> & z.infer<typeof manualFlowDataSchema>
>

const uploadFile = async (file: ArrayBuffer, fileType: FileType) => {
  const extension = fileType.split('/')[1]
  const fileName = `${randomUUID()}.${extension}`

  const result = await uploadFileToSupabase(
    {
      fileBody: file,
      filePath: `import-sources/${fileName}`,
      bucket: 'property-documents',
      type: fileType as FileType,
    },
    supabase
  )

  if (!result) {
    console.error('Error uploading file')
    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('Tiedoston tallentaminen epäonnistui')
  }

  return result.publicUrl
}

export function AddPropertyFlow({
  onFlowComplete,
  onClose: onCloseProp,
  relationType,
  isInBottomSheet,
  className,
}: {
  onFlowComplete?: (assetId: string) => void
  onClose?: () => void
  relationType?: AssetRelationType
  isInBottomSheet?: boolean
  className?: string
} = {}) {
  const { t } = useTranslations(['property', 'common'])
  const router = useRouter()
  const { showAlert } = useAlert()

  const onClose = useCallback(
    (resetFlow: () => void) => {
      resetFlow()

      if (onCloseProp) {
        onCloseProp()
      } else {
        router.replace('/(authenticated)')
      }
    },
    [router, onCloseProp]
  )

  const { mutateAsync: updateAsset } = useUpdateAsset()
  const { mutateAsync: createManualProperty } = useMutation(
    trpc.property.addManual.mutationOptions()
  )

  const handleFlowComplete = useCallback(
    async (data: unknown) => {
      const parsedData = flowDataSchema.safeParse(data)

      if (!parsedData.success) {
        console.error('Invalid flow data:', z.treeifyError(parsedData.error))

        showAlert({
          type: 'error',
          title: t(($) => $.errors.error),
          description: t(($) => $.errors.invalidData),
        })

        return false
      }

      const flowData = parsedData.data
      const isManualFlow = 'manual-input' in flowData && flowData['manual-input']

      if (isManualFlow) {
        const manualData = flowData['manual-input']

        try {
          const assetId = await createManualProperty({
            relationType: manualData.relationType,
            address: manualData.address,
            location: manualData.location,
            buildingType: manualData.buildingType,
            size: manualData.size,
            prices: manualData.prices,
            rooms: manualData.rooms,
            buildYear: manualData.buildYear,
            media: manualData.media,
            listingLink: manualData.listingLink,
            notes: manualData.notes,
          })

          if (onFlowComplete) {
            onFlowComplete(assetId)
          } else {
            router.replace(`/(authenticated)/property/${assetId}`)
          }
          return true
        } catch (error) {
          console.error('Error creating manual property:', error)

          showAlert({
            type: 'error',
            title: t(($) => $.errors.error),
            description: t(($) => $.errors.saveFailed),
          })

          return false
        }
      }

      const assetId =
        'asset-extraction' in flowData ? flowData['asset-extraction']?.assetId : undefined

      if (!assetId) {
        return false
      }

      try {
        const pricingData = 'pricing' in flowData ? flowData.pricing : undefined

        await updateAsset({
          assetId,
          assetPayload: {
            prices: pricingData,
            media: 'media' in flowData ? flowData.media?.media : undefined,
          },
        })

        if (onFlowComplete) {
          onFlowComplete(assetId)
        } else {
          router.replace(`/(authenticated)/property/${assetId}`)
        }
        return true
      } catch (error) {
        console.error('Error saving property:', error)

        showAlert({
          type: 'error',
          title: t(($) => $.errors.error),
          description: t(($) => $.errors.saveFailed),
        })

        return false
      }
    },
    [router, showAlert, updateAsset, onFlowComplete, createManualProperty, t]
  )

  const dataEntryMethod = useMemo(
    () =>
      relationType === 'prospect'
        ? 'manual'
        : relationType === 'representation'
          ? 'url'
          : undefined,
    [relationType]
  )

  const phases = useMemo(() => {
    return createFlowPhases([
      {
        id: 'data-entry-method' as const,
        title: t(($) => $.flow.dataImport),
        schema: dataEntryMethodSchema,
        condition: () => !dataEntryMethod,
        data: {
          dataEntryMethod: dataEntryMethod,
          relationType: relationType,
        },
      },
      {
        id: 'url-input' as const,
        title: t(($) => $.flow.listingUrl),
        schema: urlInputSchema,
        condition: (data) => {
          const methodData = data?.['data-entry-method']
          return methodData?.dataEntryMethod === 'url'
        },
      },
      {
        id: 'document-input' as const,
        title: t(($) => $.flow.importFromDocument),
        schema: documentInputSchema,
        condition: (data) => {
          const methodData = data?.['data-entry-method']
          return methodData?.dataEntryMethod === 'document'
        },
      },
      {
        id: 'asset-extraction' as const,
        title: t(($) => $.flow.fetchingPropertyData),
        schema: assetExtractionSchema,
        condition: (data) => {
          const methodData = data?.['data-entry-method']
          return methodData?.dataEntryMethod !== 'manual'
        },
      },
      {
        id: 'media' as const,
        title: t(($) => $.flow.propertyImages),
        schema: mediaPhaseSchema,
        condition: (data) => {
          const canCompleteFlow = flowDataSchema.safeParse(data).success

          const methodData = data?.['data-entry-method']
          return methodData?.dataEntryMethod !== 'manual' && !canCompleteFlow
        },
      },
      {
        id: 'pricing' as const,
        title: t(($) => $.flow.pricingInfo),
        schema: pricingSchema,
        condition: (data) => {
          const canCompleteFlow = flowDataSchema.safeParse(data).success

          const methodData = data?.['data-entry-method']
          return methodData?.dataEntryMethod !== 'manual' && !canCompleteFlow
        },
      },
      {
        id: 'manual-input' as const,
        title: t(($) => $.flow.propertyDetails),
        schema: propertyInputFormSchema,
        condition: (data) => {
          const methodData = data?.['data-entry-method']
          return methodData?.dataEntryMethod === 'manual'
        },
      },
    ] as const)
  }, [dataEntryMethod, relationType, t])

  return (
    <FlowProvider
      flowId="add-property"
      phases={phases}
      onComplete={handleFlowComplete}
      onClose={onClose}
      flowTitle={t(($) => $.flow.addProperty)}
      nextLabel={(currentPhaseId, _, flowData) => {
        const canCompleteFlow = flowDataSchema.safeParse(flowData).success

        if (currentPhaseId === 'manual-input') {
          return t(($) => $.actions.saveProperty)
        }

        return currentPhaseId === 'asset-extraction' && canCompleteFlow
          ? t(($) => $.actions.complete)
          : t(($) => $.common.navigation.next)
      }}
      nextButtonVariant="primary"
      isInBottomSheet={isInBottomSheet}
      className={className}>
      <AddPropertyPhaseRenderer />
    </FlowProvider>
  )
}

function AddPropertyPhaseRenderer() {
  const { t } = useTranslations('property')

  const {
    updateData,
    data,
    currentPhaseId,
    goToPhase,
    resetFlow,
    setNextPhaseAction,
    nextPhase,
    recalculateCanProceed,
  } = useFlowManager()

  const flowData = data as AddPropertyFlowData | undefined

  useEffect(() => {
    if (currentPhaseId === 'asset-extraction' && !flowData?.['asset-extraction']?.assetId) {
      void resetFlow()
    }
  }, [currentPhaseId, flowData, resetFlow])

  const { showAlert } = useAlert()

  const onPropertyExtractionFailure = useCallback(
    (asset?: ParsedAsset) => {
      // The source gets unset if it is not valid. If the source still exists, we can retry.
      const hasSource = asset && (asset.source.sourceWebUrl || asset.source.sourceDocumentUrl)

      updateData('asset-extraction', {
        assetId: hasSource ? asset?.id : undefined,
        isExtracted: false,
      })

      showAlert({
        type: 'error',
        title: t(($) => $.errors.error),
        description: !hasSource
          ? t(($) => $.errors.extractionFailedWithSource)
          : t(($) => $.errors.extractionFailed),
        cancelButton: {
          label: t(($) => $.actions.tryAnotherSource),
          onPress: async (args) => {
            if (asset?.source.sourceWebUrl) {
              goToPhase('url-input')
            } else if (asset?.source.sourceDocumentUrl) {
              goToPhase('document-input')
            } else {
              await resetFlow()
            }

            args.closeAlert()
          },
          shouldClose: false,
        },
      })
    },
    [showAlert, updateData, goToPhase, resetFlow, t]
  )

  const updateFlowDataFromAsset = useCallback(
    (asset: ParsedAsset) => {
      let updates = 0

      if (asset.relations[0]?.data?.listingDate) {
        // Prepopulate dates from asset data
        // eslint-disable-next-line i18next/no-literal-string -- technical property key
        updateData('dates', {
          listingDate: asset.relations[0]?.data?.listingDate,
          saleDate: asset.relations[0]?.data?.saleDate,
        })
        updates++
      }

      const displayPrice = getDisplayPrice(asset?.prices ?? [])
      const finalPrice = getLatestPrice(asset?.prices ?? [], 'final_price')?.price

      if (displayPrice?.price) {
        // eslint-disable-next-line i18next/no-literal-string -- technical property key
        updateData('pricing', {
          displayPrice: displayPrice.price,
          finalPrice,
        })
        updates++
      }

      if (asset.media) {
        // eslint-disable-next-line i18next/no-literal-string -- technical property key
        updateData('media', {
          media: asset.media,
        })
        updates++
      }

      return updates > 0
    },
    [updateData]
  )

  // Get pending asset state for this specific phase renderer
  const { asset, taskStatus, isMutationPending, addAsset, assetId } = usePendingAsset({
    assetId: flowData?.['asset-extraction']?.assetId,
    onSuccess: (asset) => {
      const updated = updateFlowDataFromAsset(asset)

      if (!updated && !asset.data) {
        return onPropertyExtractionFailure(asset)
      }

      updateData('asset-extraction', {
        assetId: asset.id,
        isExtracted: true,
      })

      recalculateCanProceed()
    },
    onFailure: onPropertyExtractionFailure,
  })

  useEffect(() => {
    if (asset && currentPhaseId === 'asset-extraction' && taskStatus === 'PENDING') {
      updateFlowDataFromAsset(asset)
    }
  }, [asset, updateFlowDataFromAsset, currentPhaseId, taskStatus])

  const extractPropertyData = useCallback(
    async (phaseData: AddPropertyFlowData | undefined) => {
      if (phaseData?.['asset-extraction']?.isExtracted) {
        return true
      }

      const entryMethod = phaseData?.['data-entry-method']?.dataEntryMethod
      let propertyUrl: string | undefined
      let fileUrl: string | undefined

      if (entryMethod === 'url' && phaseData?.['url-input']?.url) {
        const urlData = phaseData?.['url-input']
        propertyUrl = urlData?.url
      } else if (entryMethod === 'document' && phaseData?.['document-input']?.file) {
        const docData = phaseData?.['document-input']
        fileUrl = await uploadFile(
          docData.file.arrayBuffer,
          // eslint-disable-next-line i18next/no-literal-string -- technical MIME type
          docData.file.type || 'application/pdf'
        )
      }

      if (!propertyUrl && !fileUrl) {
        return false
      }

      try {
        const result = await addAsset({
          assetId: assetId ?? undefined,
          payload: {
            relationType: 'representation',
            source: {
              sourceWebUrl: propertyUrl,
              sourceDocumentUrl: fileUrl,
            },
            name: 'Uusi kohde',
            relationData: undefined,
          },
        })

        if (result?.assetId && ['PENDING', 'COMPLETED'].includes(result.status)) {
          updateData('asset-extraction', {
            assetId: result.assetId,
            isExtracted: result.status === 'COMPLETED',
          })

          return true
        }
      } catch (error) {
        console.error('Error saving property:', error)
      }

      onPropertyExtractionFailure()
      return false
    },
    [addAsset, assetId, onPropertyExtractionFailure, updateData]
  )

  useEffect(() => {
    if (currentPhaseId === 'url-input' || currentPhaseId === 'document-input') {
      setNextPhaseAction(extractPropertyData)
    } else {
      setNextPhaseAction(() => Promise.resolve(true))
    }
  }, [currentPhaseId, setNextPhaseAction, extractPropertyData])

  const isInputDisabled = isMutationPending

  switch (currentPhaseId) {
    case 'data-entry-method':
      return (
        <DataEntryMethodPhase
          selectedMethod={flowData?.['data-entry-method']}
          onSelect={(method) => {
            updateData('data-entry-method', {
              dataEntryMethod: method.dataEntryMethod,
              relationType: method.relationType,
            })

            if (method.relationType === 'prospect') {
              updateData('manual-input', {
                relationType: method.relationType,
              })
            }

            if (method.relationType === 'representation') {
              updateData('manual-input', {
                relationType: method.relationType,
              })
            }

            setTimeout(() => nextPhase(), 300)
          }}
        />
      )

    case 'url-input': {
      const urlData = flowData?.['url-input']

      return (
        <UrlInputPhase
          urlData={urlData}
          onUpdateData={(data) => updateData('url-input', data)}
          disabled={isInputDisabled}
        />
      )
    }

    case 'document-input': {
      const documentData = flowData?.['document-input']

      return (
        <DocumentInputPhase
          documentData={documentData}
          onUpdateData={(data) => updateData('document-input', data)}
          disabled={isInputDisabled}
        />
      )
    }

    case 'asset-extraction': {
      return (
        <AssetExtractionPhase
          flowData={flowData}
          pendingAsset={{
            asset: asset || undefined,
            taskStatus,
            isMutationPending,
          }}
        />
      )
    }

    case 'media':
      return (
        <MediaPhase
          mediaData={flowData?.media ?? { media: [] }}
          // eslint-disable-next-line i18next/no-literal-string -- technical property key
          onMediaUpdate={(media) => updateData('media', { media })}
        />
      )

    case 'pricing': {
      return (
        <PricingPhase
          relationType={flowData?.['data-entry-method']?.relationType ?? 'representation'}
          priceValues={
            flowData?.pricing ?? {
              displayPrice: 0,
            }
          }
          // eslint-disable-next-line i18next/no-literal-string -- technical property key
          onUpdateData={(data) => updateData('pricing', data)}
        />
      )
    }

    case 'manual-input': {
      return (
        <ManualPropertyInputPhase
          data={
            flowData?.['manual-input'] ?? {
              relationType: 'prospect',
            }
          }
          onUpdateData={(data) => updateData('manual-input', data)}
        />
      )
    }

    default:
      return (
        <Text>
          {t(($) => $.flow.unknownPhase)}: {currentPhaseId}
        </Text>
      )
  }
}

function AssetExtractionPhase({
  flowData,
  pendingAsset,
}: {
  flowData: AddPropertyFlowData | undefined
  pendingAsset: {
    asset: ParsedAsset | undefined
    taskStatus: string
    isMutationPending: boolean
  }
}) {
  const { asset, taskStatus } = pendingAsset
  const priceValue = flowData?.pricing?.displayPrice ?? 0

  const assetData = useMemo(() => {
    if (!asset) {
      return undefined
    }

    return {
      ...asset,
      prices: asset.prices ?? [
        {
          price: priceValue ?? 0,
          price_type: 'asking_price' as const,
          created_at: new Date().toISOString(),
          asset_id: asset.id,
          id: randomUUID(),
          currency: 'EUR',
          deleted_at: null,
          user_id: null,
        },
      ],
    } satisfies ParsedAsset
  }, [asset, priceValue])

  const title = createAddressTitle(asset?.address) || asset?.name

  const canCompleteFlow = useMemo(() => flowDataSchema.safeParse(flowData).success, [flowData])

  const { t } = useTranslations('property')

  return (
    <PhaseWrapper>
      <ViewTitle>{!asset?.data ? t(($) => $.flow.fetchingPropertyData) : title}</ViewTitle>
      {assetData ? (
        <PropertyCard asset={assetData} isLoading={taskStatus === 'PENDING'} />
      ) : (
        <LoadingAsset />
      )}
      {taskStatus === 'PENDING' && (
        <View className="gap-2">
          <Body className="mb-2 text-center text-sm">{t(($) => $.flow.waitMinutes)}</Body>
          <ActivityIndicator />
        </View>
      )}
      {taskStatus === 'COMPLETED' && canCompleteFlow && (
        <View className="gap-4 px-6">
          <Body className="text-center">{t(($) => $.flow.dataFetched)}</Body>
        </View>
      )}
    </PhaseWrapper>
  )
}
